import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";

const LOGO_BLUE = "/ofa-france-logo-on-light.png";
const LOGO_DARK = "/ofa-france-logo-on-light.png";

export function Header() {
  const { lang, setLang, t } = useLang();
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const onAdmin = location.pathname.startsWith("/admin");

  if (onAdmin) return null;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/85 border-b border-border">
      {/* Top utility bar — Family Hub brand identity */}
      <div className="bg-[hsl(var(--brand-navy))] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-9 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-semibold">
          <span data-testid="header-tagline" className="flex items-center gap-2 truncate">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse shrink-0" />
            <span className="text-accent">{lang === "fr" ? "OFA Portail Familles" : "OFA Family Hub"}</span>
          </span>
          <span data-testid="header-accreditation" className="hidden md:flex items-center gap-2 text-white/80 shrink-0">
            {lang === "fr" ? "École en ligne accréditée aux USA" : "Online school accredited in the USA"}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <img src={LOGO_DARK} alt="OFA France" className="h-12 w-auto" data-testid="brand-logo" />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
          <Link to="/" data-testid="nav-home" className="hover:text-primary transition-colors">{t("nav.home")}</Link>
          <Link to="/inquiry" data-testid="nav-inquiry" className="hover:text-primary transition-colors">{t("nav.inquiry")}</Link>
          <Link
            to="/recommender"
            data-testid="nav-recommender"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors"
            style={{
              borderColor: "hsl(var(--brand-lavender) / 0.45)",
              backgroundColor: "hsl(var(--brand-lavender) / 0.12)",
              color: "hsl(var(--brand-lavender))",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "hsl(var(--brand-lavender))";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "hsl(var(--brand-lavender) / 0.12)";
              e.currentTarget.style.color = "hsl(var(--brand-lavender))";
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth="2"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
            {lang === "fr" ? "Quelle voie\u00A0?" : "Which track?"}
          </Link>
          {user && !isAdmin && (
            <Link to="/parent" data-testid="nav-parent-dashboard" className="hover:text-primary transition-colors">{t("nav.dashboard")}</Link>
          )}
          {isAdmin && (
            <Link to="/admin" data-testid="nav-admin-dashboard" className="hover:text-primary transition-colors">{t("nav.dashboard")}</Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center text-[11px] font-semibold border border-border rounded-full p-0.5" data-testid="lang-switcher">
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-2.5 py-0.5 rounded-full transition-colors ${lang === "fr" ? "bg-[hsl(var(--brand-navy))] text-white" : "text-muted-foreground hover:text-foreground"}`}
            >FR</button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-0.5 rounded-full transition-colors ${lang === "en" ? "bg-[hsl(var(--brand-navy))] text-white" : "text-muted-foreground hover:text-foreground"}`}
            >EN</button>
          </div>
          {!user ? (
            <>
              <Link to="/login" data-testid="header-login-link" className="hidden sm:inline-block">
                <Button variant="ghost" size="sm">{t("nav.login")}</Button>
              </Link>
              <Link to="/register" data-testid="header-register-link">
                <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-5 shadow-sm">
                  {t("nav.signup")}
                </Button>
              </Link>
            </>
          ) : (
            <Button variant="outline" size="sm" data-testid="header-logout" onClick={() => { logout(); window.location.href="/"; }}>{t("nav.logout")}</Button>
          )}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { t, lang } = useLang();
  return (
    <footer className="border-t border-border mt-24 bg-[hsl(var(--brand-navy))] text-white/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-4 gap-8">
        <div>
          <img src="/ofa-france-logo-on-dark.png" alt="OFA France" className="h-12 w-auto inline-block" />
          <div className="mt-4 text-xs tracking-[0.18em] uppercase text-white/60 font-semibold">
            France · Family Connect
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/70 max-w-xs">{t("landing.footerNote")}</p>
        </div>
        <div className="text-sm space-y-2">
          <div className="text-white font-semibold mb-2">{lang === "fr" ? "Plateforme" : "Platform"}</div>
          <div><Link to="/" className="hover:text-white">{t("nav.home")}</Link></div>
          <div><Link to="/recommender" className="hover:text-white">{lang === "fr" ? "Quelle voie ?" : "Which track?"}</Link></div>
          <div><Link to="/inquiry" className="hover:text-white">{t("nav.inquiry")}</Link></div>
          <div><Link to="/register" className="hover:text-white">{t("nav.signup")}</Link></div>
        </div>
        <div className="text-sm space-y-2">
          <div className="text-white font-semibold mb-2">{lang === "fr" ? "Nous contacter" : "Contact us"}</div>
          <div>
            <a
              href="mailto:info@ofalycee.fr"
              data-testid="footer-contact-email"
              className="hover:text-white text-white/80 break-all"
            >
              info@ofalycee.fr
            </a>
          </div>
          <div className="text-white/60 text-xs leading-relaxed pt-1">
            {lang === "fr"
              ? "Réponse sous 48h ouvrées · Français & English"
              : "Reply within 48 business hours · English & Français"}
          </div>
          <div className="pt-3">
            <a
              href="https://ofalycee.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white text-xs underline-offset-2 hover:underline"
            >
              {lang === "fr" ? "Site OFA France →" : "OFA France website →"}
            </a>
          </div>
        </div>
        <div className="text-sm space-y-2">
          <div className="text-white font-semibold mb-2">{lang === "fr" ? "Reconnaissance" : "Recognition"}</div>
          <div className="text-white/70">COGNIA accredited</div>
          <div className="text-white/70">US High School Diploma</div>
          <div className="pt-3 space-y-1.5 border-t border-white/10 mt-3">
            <div><Link to="/privacy" className="hover:text-white text-white/70" data-testid="footer-privacy">{lang === "fr" ? "Politique de confidentialité" : "Privacy Policy"}</Link></div>
            <div><Link to="/terms" className="hover:text-white text-white/70" data-testid="footer-terms">{lang === "fr" ? "Conditions d'utilisation" : "Terms of Use"}</Link></div>
          </div>
          <div className="text-white/70 pt-3">© {new Date().getFullYear()} OFA France</div>
        </div>
      </div>
      {/* Legal info bar — US entity disclosure for FR consumers */}
      <div className="border-t border-white/10 bg-black/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 text-[11px] leading-relaxed text-white/55">
          {lang === "fr" ? (
            <>
              Service fourni par <strong className="text-white/75">OFALycée, Inc.</strong> · EIN 85-3923807 · 27 Berkshire Drive, Winchester MA 01890, États-Unis · Contact :{" "}
              <a href="mailto:info@ofalycee.fr" className="underline hover:text-white">info@ofalycee.fr</a>.
              <br />
              École d'enseignement en ligne en direct accréditée COGNIA. TVA non applicable — service exonéré au titre de l'Article 132(1)(i) de la Directive 2006/112/CE et de l'Article 261-4-4° du CGI. Pour les résidents en France, les dispositions impératives du Code de la consommation s'appliquent (droit de rétractation 14 jours — Art. L221-18).
            </>
          ) : (
            <>
              Service operated by <strong className="text-white/75">OFALycée, Inc.</strong> · EIN 85-3923807 · 27 Berkshire Drive, Winchester MA 01890, USA · Contact:{" "}
              <a href="mailto:info@ofalycee.fr" className="underline hover:text-white">info@ofalycee.fr</a>.
              <br />
              COGNIA-accredited online live school. VAT not applicable — service exempt under Article 132(1)(i) of EU Directive 2006/112/EC and Article 261-4-4° of the French Tax Code. For residents in France, mandatory provisions of the French Consumer Code apply (14-day withdrawal right — Art. L221-18).
            </>
          )}
        </div>
      </div>
    </footer>
  );
}
